"use client";

import React, { useEffect, useRef, useState, useTransition } from "react";
import styles from "./MaterialsLibrary.module.scss";
import { Button, Flex, Form, Grid, Menu, MenuProps, Switch, Tour, TourProps, Typography } from "antd/lib";
import { useTranslation } from "react-i18next";
import useOutsideClick from "@/hooks/useOutsideClick";
import XStroke from "@/public/icons/XStroke";
import { useSidebarTutorial } from "@/context/SidebarTutorialContext";
import { SidebarTutorialSlugs } from "@/constants/tour";
import { useMaterialsFetchers } from "@/hooks/useMaterialsFetchers";
import dynamic from "next/dynamic";
import { buildUrlWithParams, lazyRetry } from "@/utils/helpers";
import Filter from "@/src/components/MaterialsLibrary/Filter";
import { useRouter, useSearchParams } from "next/navigation";
import { useFilter } from "@/hooks/useFilter";
import { CheckboxOptionType } from "antd/lib/checkbox/Group";
import { FilterTypes, LocalStorageKeys, SearchParams } from "@/constants/common";
import { useAuth } from "@/context/AuthContext";
import { usePath } from "@/hooks/usePath";
import { useSearch } from "@/hooks/useSearch";
import Search from "@/src/components/Common/Search";

type SearchComponentProps = {
  isSearchAndFilterHidden?: boolean;
};

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: "group",
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
  } as MenuItem;
}

const MaterialsTree = dynamic(() => lazyRetry(() => import("./MaterialsTree")), {
  ssr: false,
});

const NoResultsFound = dynamic(() => lazyRetry(() => import("./NoResultsFound")), {
  ssr: false,
});

const { useBreakpoint } = Grid;

const SearchComponent: React.FC<SearchComponentProps> = ({ isSearchAndFilterHidden }) => {
  const { t } = useTranslation();
  const { pageTourIsOpen, tourWasShown, setSidebarTutorialSlug, setTourWasShown } = useSidebarTutorial();
  const { fetchAndGroupMaterialsNewApi } = useMaterialsFetchers();
  const screens = useBreakpoint();
  const searchParams = useSearchParams();
  const pathname = usePath();
  const [form] = Form.useForm();
  const { meDataError } = useAuth();

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  const [materialsTree, setMaterialsTree] = useState<any>([]);
  const [showToggles, setShowToggles] = useState(false);
  const [tourIsOpen, setTourIsOpen] = useState<boolean>(pageTourIsOpen);
  const [materialsTreeIsRendered, setMaterialsTreeIsRendered] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filterDataIsLoading, setFilterDataIsLoading] = useState(true);
  const [activityLanguages, setActivityLanguages] = useState<CheckboxOptionType[]>([]);
  const [filtersOptionsError, setFiltersOptionsError] = useState(false);
  const [searchParamsEffectCanBeTriggered, setSearchParamsEffectCanBeTriggered] = useState(true);
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [descriptionsEnabled, setDescriptionsEnabled] = useState(() => {
    const saved = localStorage.getItem(LocalStorageKeys.descriptionsEnabled);
    return saved ? JSON.parse(saved) : true;
  });

  const [languagesEnabled, setLanguagesEnabled] = useState(() => {
    const saved = localStorage.getItem(LocalStorageKeys.languagesEnabled);
    return saved ? JSON.parse(saved) : true;
  });

  const [materialsCountEnabled, setMaterialsCountEnabled] = useState(() => {
    const saved = localStorage.getItem(LocalStorageKeys.materialsCountEnabled);
    return saved ? JSON.parse(saved) : true;
  });

  const [isLoading, setIsLoading] = useState(true);

  const referralToken = searchParams.get(SearchParams.referralToken);

  const {
    searchParamsWillBeAdded,
    applyFiltersHandler,
    resetFiltersHandler,
    formValuesChanged,
    formValuesChangeHandler,
    filtersCleared,
  } = useFilter(form, filterDataIsLoading, FilterTypes.materials, activityLanguages);

  const { showSearchInputReset } = useSearch(FilterTypes.materials);

  const { searchValue, searchInputChangeHandler, searchHandler, resetInputSearchHandler, searchUrlParam } = useSearch(
    FilterTypes.common,
  );

  const abortController = new AbortController();

  const steps: TourProps["steps"] = [
    {
      title: t("common.tour.materialsLibrary.steps.search.title"),
      description: t("common.tour.materialsLibrary.steps.search.description"),
      target: () => ref2.current,
    },
    {
      title: t("common.tour.materialsLibrary.steps.filters.title"),
      description: t("common.tour.materialsLibrary.steps.filters.description"),
      target: () => ref3.current,
      nextButtonProps: {
        children: <span>{t("common.tour.buttons.next")}</span>,
      },
    },
  ];

  if (ref1.current) {
    steps.unshift({
      title: t("common.tour.materialsLibrary.steps.favorite.title"),
      description: t("common.tour.materialsLibrary.steps.favorite.description"),
      target: () => ref1.current,
    });
  }

  useOutsideClick(
    menuRef,
    () => {
      setShowToggles(false);
    },
    btnRef,
  );

  useEffect(() => {
    if (tourIsOpen !== pageTourIsOpen) setTourIsOpen(pageTourIsOpen);
  }, [pageTourIsOpen]);

  useEffect(() => {
    localStorage.setItem(LocalStorageKeys.descriptionsEnabled, JSON.stringify(descriptionsEnabled));
  }, [descriptionsEnabled]);

  useEffect(() => {
    localStorage.setItem(LocalStorageKeys.languagesEnabled, JSON.stringify(languagesEnabled));
  }, [languagesEnabled]);

  useEffect(() => {
    localStorage.setItem(LocalStorageKeys.materialsCountEnabled, JSON.stringify(materialsCountEnabled));
  }, [materialsCountEnabled]);

  useEffect(() => {
    if (searchParamsWillBeAdded || !searchParamsEffectCanBeTriggered) return;

    if (!isLoading) setIsLoading(true);

    fetchAndGroupMaterialsNewApi(Object.fromEntries(searchParams), false, setIsLoading, abortController.signal).then(
      (res) => {
        if (!res) return;

        setMaterialsTree(res.data);
        setIsLoading(false);

        if (referralToken) {
          const currentParams = new URLSearchParams(searchParams.toString());
          currentParams.delete(SearchParams.referralToken);

          setSearchParamsEffectCanBeTriggered(false);
          startTransition(() =>
            router.push(buildUrlWithParams(pathname, "", Object.fromEntries(currentParams.entries()))),
          );
        }
      },
    );

    return () => {
      abortController.abort();
    };
  }, [searchParams, searchParamsWillBeAdded]);

  useEffect(() => {
    if (isPending || referralToken || searchParamsEffectCanBeTriggered) return;

    setSearchParamsEffectCanBeTriggered(true);
  }, [referralToken]);

  useEffect(() => {
    if (!meDataError && !filtersOptionsError) return;

    setIsLoading(false);
  }, [meDataError, filtersOptionsError]);

  const items: MenuProps["items"] = [
    getItem(
      <div>
        <Switch
          checked={descriptionsEnabled}
          onChange={() => setDescriptionsEnabled((prev: boolean) => !prev)}
          style={{ marginRight: 8 }}
        />
        <Typography.Text>{t("common.settings.materialsLibrary.descriptions")}</Typography.Text>
      </div>,
      "1",
    ),
    getItem(
      <div>
        <Switch
          checked={languagesEnabled}
          onChange={() => setLanguagesEnabled((prev: boolean) => !prev)}
          style={{ marginRight: 8 }}
        />
        <Typography.Text>{t("common.settings.materialsLibrary.languages")}</Typography.Text>
      </div>,
      "2",
    ),
    getItem(
      <div>
        <Switch
          checked={materialsCountEnabled}
          onChange={() => setMaterialsCountEnabled((prev: boolean) => !prev)}
          style={{ marginRight: 8 }}
        />
        <Typography.Text>{t("common.settings.materialsLibrary.materialsCount")}</Typography.Text>
      </div>,
      "3",
    ),
  ];

  return (
    <div className={styles.searchComponent}>
      <Flex vertical className="w-full">
        {!isSearchAndFilterHidden && (
          <Flex
            gap={16}
            justify="space-between"
            align={screens.lg ? "center" : "flex-start"}
            style={{
              padding: "0 0 32px 0",
            }}
            vertical={!screens.sm}
          >
            <Flex gap={8} align="start" justify="start" ref={ref2} className={screens.sm ? "" : "w-full"}>
              <Search filterType={FilterTypes.materials} />
            </Flex>
            <Flex gap={16}>
              <Flex vertical>
                <Button
                  ref={btnRef}
                  className={styles.buttonFlex}
                  style={{ display: "flex", alignItems: "center" }}
                  onClick={() => setShowToggles((prev) => !prev)}
                >
                  {t("materialsLibraryPage.settingsBtn")}
                </Button>
                {showToggles && (
                  <div ref={menuRef} className={showToggles ? styles.menuEnter : styles.menuExit}>
                    <Menu className={styles.settingsMenu} items={items} />
                  </div>
                )}
              </Flex>
              {!screens.xl && <Button onClick={() => setIsDrawerOpen(true)}>{t("common.filters.title")}</Button>}
            </Flex>
          </Flex>
        )}

        {!!materialsTree.length || isLoading ? (
          <MaterialsTree
            materials={materialsTree}
            descriptionsEnabled={descriptionsEnabled}
            languagesEnabled={languagesEnabled}
            materialsCountEnabled={materialsCountEnabled}
            quickSearchEnabled
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            firstFavoriteButtonRef={ref1}
            setMaterialsTreeIsRendered={setMaterialsTreeIsRendered}
          />
        ) : (
          <NoResultsFound />
        )}
      </Flex>
      {!isSearchAndFilterHidden && (
        <>
          <div ref={ref3}>
            <Filter
              form={form}
              isLoading={filterDataIsLoading}
              isDrawerOpen={isDrawerOpen}
              applyFiltersHandler={applyFiltersHandler}
              resetFiltersHandler={resetFiltersHandler}
              setIsLoading={setFilterDataIsLoading}
              setIsDrawerOpen={setIsDrawerOpen}
              setActivityLanguages={setActivityLanguages}
              formValuesChanged={formValuesChanged}
              formValuesChangeHandler={formValuesChangeHandler}
              filtersCleared={filtersCleared}
              setFiltersOptionsError={setFiltersOptionsError}
            />
          </div>

          {!isLoading && (!materialsTree.length || materialsTreeIsRendered) && (
            <Tour
              placement="right"
              open={tourIsOpen}
              closeIcon={
                <XStroke
                  color="#000"
                  onClick={() => {
                    setTourIsOpen(false);
                    if (!tourWasShown.openTutorialIcon) setSidebarTutorialSlug(SidebarTutorialSlugs.openTutorialIcon);
                    setTourWasShown({ ...tourWasShown, main: true });
                  }}
                />
              }
              onFinish={() => {
                setTourIsOpen(false);
                setSidebarTutorialSlug(SidebarTutorialSlugs.publicEvents);
              }}
              steps={steps}
              indicatorsRender={() => <></>}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SearchComponent;
